import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const COLORS = ['#1976d2', '#2e7d32', '#6d4c41', '#ed6c02', '#8e24aa', '#455a64', '#00897b', '#5d4037'];
const pct = (value, total) => { if (total === undefined) return `${Math.round((value||0)*100)}%`; if (!total) return '0%'; return `${((value/total)*100).toFixed(0)}%`; };
const toBytes = (n) => Number.isFinite(n)?n:0;
const formatBytes = (bytes) => { bytes=toBytes(bytes); if(bytes===0) return '0 B'; const k=1024; const sizes=['B','KB','MB','GB','TB','PB']; const i=Math.floor(Math.log(bytes)/Math.log(k)); const val=bytes/Math.pow(k,i); return `${val.toFixed(val>=100?0:1)} ${sizes[i]}`; };

export default function ProxmoxClusterSummary(){
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading,setLoading]=useState(true); 
  const [error,setError]=useState(''); 
  const [data,setData]=useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('/api/proxmox/summary?period=previousMonth');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setData(await resp.json());
      } catch (e) {
          Sentry.captureException(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  // Calculate infrastructure health score
  const healthScore = useMemo(() => {
    if (!data?.totals) return null;
    
    // Node Availability: 50% weight (critical for redundancy)
    const nodeAvailability = data.totals.nodeCount > 0 
      ? (data.totals.onlineNodes / data.totals.nodeCount) * 100 
      : 0;
    const nodeAvailabilityScore = nodeAvailability;
    
    // Capacity Headroom: 50% weight (ability to handle growth/failures)
    const cpuUsed = data.totals.cpuUsagePercent || 0;
    const memUsed = data.totals.memUsedPercent || 0;
    const avgUtilization = (cpuUsed + memUsed) / 2;
    const capacityHeadroom = 100 - avgUtilization;
    const capacityScore = capacityHeadroom;
    
    const overall = (nodeAvailabilityScore * 0.5) + (capacityScore * 0.5);
    return Math.round(overall);
  }, [data]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 80) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 60) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 40) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 20) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const vmStatusData = useMemo(()=> data?.totals?.vmByStatus? Object.entries(data.totals.vmByStatus).map(([name,value])=>({ name,value })):[], [data]);
  const vmTypeData = useMemo(()=> data?.totals?.vmByType? Object.entries(data.totals.vmByType).map(([name,value])=>({ name,value })):[], [data]);
  
  if(loading) return <HexagonLoader/>; 
  if(error) return <div style={{ color:'red' }}>{error}</div>; 
  if(!data) return null;
  
  const { totals, nodes=[], vms=[], top={}, nodeSummaries=[], period } = data; 
  const periodLabel = period?.label? `${period.label} (snapshot)`:null;
  const Progress = ({ percent, color='#1976d2' }) => (<div style={{ background:'#eee', height:6, borderRadius:6 }}><div style={{ width:`${Math.min(100,Math.max(0,percent))}%`, height:'100%', background:color, borderRadius:6 }} /></div>);
  
  const memUsedPct = totals?.memUsedPercent ?? 0; 
  const cpuUsedPct = totals?.cpuUsagePercent ?? 0;
  const nodeAvailPct = totals.nodeCount > 0 ? (totals.onlineNodes / totals.nodeCount) * 100 : 0;
  const capacityHeadroom = 100 - ((cpuUsedPct + memUsedPct) / 2);
  const scoreInterp = getScoreInterpretation(healthScore);
  const offlineNodes = (totals.nodeCount || 0) - (totals.onlineNodes || 0);
  const stoppedVMs = (totals.vmCount || 0) - (totals.runningVms || 0);

  const llmProxyText = [ 
    `Infrastructure Health Score: ${healthScore}/100`,
    `Nodes online: ${totals?.onlineNodes}/${totals?.nodeCount}`, 
    `VMs running: ${totals?.runningVms}/${totals?.vmCount}`, 
    `CPU usage: ${cpuUsedPct.toFixed?.(0)??0}%`, 
    `Memory usage: ${memUsedPct.toFixed?.(0)??0}%`, 
    periodLabel?`Context: ${periodLabel}`:null 
  ].filter(Boolean).join('\n');

  return (
    <div className="dashboard-card group-wrapper proxmox-cluster-summary">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0, marginBottom: 12 }}>Infrastructure & Virtualization</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Infrastructure Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> Your Proxmox cluster (Athena) has <strong>{totals.onlineNodes}/{totals.nodeCount} nodes online</strong> running <strong>{totals.runningVms} virtual machines</strong>. Infrastructure is at <strong>{((cpuUsedPct + memUsedPct) / 2).toFixed(0)}% average utilization</strong> with <strong>{capacityHeadroom.toFixed(0)}% capacity remaining</strong>.
              {nodeAvailPct === 100 && capacityHeadroom > 30 && ' Your infrastructure is healthy with good redundancy and capacity.'}
              {offlineNodes > 0 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {offlineNodes} node{offlineNodes > 1 ? 's' : ''} offline - redundancy compromised!</span>}
              {capacityHeadroom < 20 && <span style={{ color: '#ff5722', fontWeight: 600 }}> Running low on compute resources - plan capacity expansion.</span>}
              {(cpuUsedPct > 80 || memUsedPct > 80) && <span style={{ color: '#ffa000', fontWeight: 600 }}> High resource utilization may impact performance.</span>}
            </div>
          </div>
        </div>
      </div>

      {periodLabel && <div style={{ margin: '-8px 0 12px 0', fontSize:10, color:'#777' }}>{periodLabel}</div>}

      {/* Key Metrics */}
      {totals && (
        <div className="metrics-row">
          <div className="dashboard-card metric-card" title="Infrastructure nodes online and available">
            <div className="metric-label">Node Availability</div>
            <div className="metric-value--md" style={{ fontWeight:700, color: nodeAvailPct === 100 ? '#2e7d32' : nodeAvailPct >= 75 ? '#ffa000' : '#d32f2f' }}>
              {totals.onlineNodes}/{totals.nodeCount}
            </div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>{nodeAvailPct.toFixed(0)}% online</div>
          </div>
          <div className="dashboard-card metric-card" title="Virtual machines running vs total provisioned">
            <div className="metric-label">VMs Running</div>
            <div className="metric-value--md" style={{ fontWeight:700, color: stoppedVMs === 0 ? '#2e7d32' : '#ffa000' }}>
              {totals.runningVms}/{totals.vmCount}
            </div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>
              {stoppedVMs > 0 ? `${stoppedVMs} stopped` : 'All running'}
            </div>
          </div>
          <div className="dashboard-card metric-card" title="Current CPU utilization across cluster">
            <div className="metric-label">CPU Utilization</div>
            <div className="metric-value--sm" style={{ color: cpuUsedPct < 70 ? '#2e7d32' : cpuUsedPct < 85 ? '#ffa000' : '#d32f2f' }}>
              {cpuUsedPct.toFixed(0)}%
            </div>
            <div style={{ marginTop:6 }} className="ProgressBar"><Progress percent={cpuUsedPct} color={cpuUsedPct < 70 ? '#2e7d32' : cpuUsedPct < 85 ? '#ef6c00' : '#d32f2f'}/></div>
            <div className="metric-subtitle" style={{ marginTop:4 }}>{totals.cpuCoresTotal ?? 0} total cores</div>
          </div>
          <div className="dashboard-card metric-card" title="Current memory utilization across cluster">
            <div className="metric-label">Memory Utilization</div>
            <div className="metric-value--sm" style={{ color: memUsedPct < 70 ? '#2e7d32' : memUsedPct < 85 ? '#ffa000' : '#d32f2f' }}>
              {memUsedPct.toFixed(0)}%
            </div>
            <div style={{ marginTop:6 }} className="ProgressBar"><Progress percent={memUsedPct} color={memUsedPct < 70 ? '#2e7d32' : memUsedPct < 85 ? '#ef6c00' : '#d32f2f'}/></div>
            <div className="metric-subtitle" style={{ marginTop:4 }}>{formatBytes(totals.memUsedBytes)} / {formatBytes(totals.memTotalBytes)}</div>
          </div>
          <div className="dashboard-card metric-card" title="Available capacity for growth or handling node failures">
            <div className="metric-label">Capacity Headroom</div>
            <div className="metric-value--md" style={{ fontWeight:700, color: capacityHeadroom > 30 ? '#2e7d32' : capacityHeadroom > 15 ? '#ffa000' : '#d32f2f' }}>
              {capacityHeadroom.toFixed(0)}%
            </div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>Resources available</div>
          </div>
        </div>
      )}

      {/* Node Status Table */}
      <div className="dashboard-card">
        <h4 className="chart-title">Cluster Nodes</h4>
        <div className="chart-subtitle">Individual node health and resource utilization</div>
        <div style={{ overflowX:'auto' }}>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '6px' }}>Node Name</th>
                <th style={{ padding: '6px' }}>Status</th>
                <th style={{ padding: '6px' }}>CPU Usage</th>
                <th style={{ padding: '6px' }}>Memory Usage</th>
                <th style={{ padding: '6px' }}>Uptime</th>
                <th style={{ padding: '6px' }}>VMs (Running/Total)</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((n,idx)=>{ 
                const memPct=pct(n.mem,n.maxmem); 
                const cpuPct = n.maxcpu>0? `${Math.round((n.cpu||0)*100)}%`:'0%'; 
                const nodeVms=vms.filter(v=>v.node===n.node); 
                const isOffline = n.status !== 'online';
                return (
                  <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign:'left', padding: '6px', fontWeight: 600 }}>{n.node}</td>
                    <td style={{ padding: '6px', color: isOffline ? '#d32f2f' : '#2e7d32', fontWeight: 600 }}>{n.status}</td>
                    <td style={{ padding: '6px' }}>{cpuPct}</td>
                    <td style={{ padding: '6px' }}>{memPct}</td>
                    <td style={{ padding: '6px' }}>{Math.floor((n.uptime||0)/3600)}h</td>
                    <td style={{ padding: '6px' }}>{nodeVms.filter(v=>v.status==='running').length}/{nodeVms.length}</td>
                  </tr>
                ); 
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* VM Distribution Charts */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Virtual Machines by Status</h4>
          <div className="chart-subtitle">VM operational state distribution</div>
          {vmStatusData.length===0 && <div style={{ fontSize:11, color:'#777', padding: 20, textAlign: 'center' }}>No VM data available.</div>}
          {vmStatusData.length>0 && (
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={150} minHeight={150}>
              <BarChart data={vmStatusData} layout="vertical" margin={{ left: 16, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#1976d2" radius={[3,3,3,3]} isAnimationActive={!isPrintMode} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Virtual Machines by Type</h4>
          <div className="chart-subtitle">VM technology distribution (QEMU, LXC, etc.)</div>
          {vmTypeData.length===0 && <div style={{ fontSize:11, color:'#777', padding: 20, textAlign: 'center' }}>No VM type data available.</div>}
          {vmTypeData.length>0 && (
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={150} minHeight={150}>
              <PieChart>
                <Pie data={vmTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={(d) => `${d.name} (${d.value})`} isAnimationActive={!isPrintMode}>
                  {vmTypeData.map((e,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top Resource Consumers */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Top VMs by CPU Usage</h4>
          <div className="chart-subtitle">Highest CPU consumers - potential optimization targets</div>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '4px 6px' }}>VM Name</th>
                <th style={{ padding: '4px 6px' }}>Node</th>
                <th style={{ padding: '4px 6px' }}>CPU %</th>
              </tr>
            </thead>
            <tbody>
              {(top.byCpu||[]).map((v,i)=>(
                <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign:'left', padding: '4px 6px', fontWeight: 500 }}>{v.name||v.id}</td>
                  <td style={{ padding: '4px 6px' }}>{v.node}</td>
                  <td style={{ padding: '4px 6px', fontWeight: 600, color: (v.cpuPercent||0) > 80 ? '#d32f2f' : (v.cpuPercent||0) > 60 ? '#ffa000' : '#666' }}>
                    {(v.cpuPercent??0).toFixed(1)}%
                  </td>
                </tr>
              ))}
              {(!top.byCpu||top.byCpu.length===0)&&<tr><td colSpan={3} style={{ textAlign:'center', color:'#777', padding: 12 }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Top VMs by Memory Usage</h4>
          <div className="chart-subtitle">Highest memory consumers - capacity planning insights</div>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '4px 6px' }}>VM Name</th>
                <th style={{ padding: '4px 6px' }}>Node</th>
                <th style={{ padding: '4px 6px' }}>Memory %</th>
              </tr>
            </thead>
            <tbody>
              {(top.byMem||[]).map((v,i)=>(
                <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign:'left', padding: '4px 6px', fontWeight: 500 }}>{v.name||v.id}</td>
                  <td style={{ padding: '4px 6px' }}>{v.node}</td>
                  <td style={{ padding: '4px 6px', fontWeight: 600, color: (v.memPercent||0) > 80 ? '#d32f2f' : (v.memPercent||0) > 60 ? '#ffa000' : '#666' }}>
                    {(v.memPercent??0).toFixed(1)}%
                  </td>
                </tr>
              ))}
              {(!top.byMem||top.byMem.length===0)&&<tr><td colSpan={3} style={{ textAlign:'center', color:'#777', padding: 12 }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-Node Summary */}
      {nodeSummaries?.length>0 && (
        <div className="dashboard-card">
          <h4 className="chart-title">Per-Node Resource Summary</h4>
          <div className="chart-subtitle">Detailed breakdown by infrastructure node</div>
          <div style={{ overflowX:'auto' }}>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign:'left', padding: '6px' }}>Node</th>
                  <th style={{ padding: '6px' }}>VMs (Running/Total)</th>
                  <th style={{ padding: '6px' }}>CPU Usage</th>
                  <th style={{ padding: '6px' }}>Memory Usage</th>
                  <th style={{ padding: '6px' }}>Uptime</th>
                </tr>
              </thead>
              <tbody>
                {nodeSummaries.map((n,i)=>(
                  <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign:'left', padding: '6px', fontWeight: 600 }}>{n.node}</td>
                    <td style={{ padding: '6px' }}>{n.vmTotals?.running??0}/{n.vmTotals?.total??0}</td>
                    <td style={{ padding: '6px', color: (n.cpuUsagePercent||0) > 80 ? '#d32f2f' : (n.cpuUsagePercent||0) > 60 ? '#ffa000' : '#666' }}>
                      {(n.cpuUsagePercent??0).toFixed(0)}%
                    </td>
                    <td style={{ padding: '6px', color: (n.memUsagePercent||0) > 80 ? '#d32f2f' : (n.memUsagePercent||0) > 60 ? '#ffa000' : '#666' }}>
                      {(n.memUsagePercent??0).toFixed(0)}%
                    </td>
                    <td style={{ padding: '6px' }}>{Math.floor((n.uptime||0)/3600)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <ul>
          <li><strong>Infrastructure Health Score:</strong> Combines Node Availability (50%) and Capacity Headroom (50%). Target is 80%+ for robust operations.</li>
          <li><strong>Node Availability:</strong> Percentage of cluster nodes online. {'<'}100% means reduced redundancy - risk if another node fails.</li>
          <li><strong>Capacity Headroom:</strong> Remaining resources (100 - avg utilization). {'>'}30% is ideal for handling spikes and growth.</li>
          <li><strong>CPU/Memory Utilization:</strong> Current resource consumption. {'>'}80% indicates performance risk and limited growth capacity.</li>
          <li><strong>VM Status:</strong> Running VMs provide services. Stopped VMs may be unused (candidates for cleanup) or intentionally offline.</li>
          <li><strong>Top Consumers:</strong> VMs using most resources. High utilization may indicate optimization opportunities or need for dedicated hardware.</li>
          <li><strong>Cluster (Athena):</strong> Your virtualization infrastructure. All business-critical services depend on this platform.</li>
        </ul>
        {offlineNodes > 0 && (
          <div className="action-callout">
            <strong>Action Required:</strong> {offlineNodes} cluster node{offlineNodes > 1 ? 's are' : ' is'} offline. This reduces redundancy and fault tolerance. If another node fails, services may be impacted. Investigate and restore offline node{offlineNodes > 1 ? 's' : ''} immediately.
          </div>
        )}
        {capacityHeadroom < 20 && (
          <div className="action-callout">
            <strong>Capacity Warning:</strong> Only {capacityHeadroom.toFixed(0)}% headroom remaining. Limited ability to handle growth, workload spikes, or node failures. Plan infrastructure expansion or optimize/retire underutilized VMs.
          </div>
        )}
      </div>
    </div>
  );
}
